import { useCallback, useEffect, useRef } from 'react';
import { stripMarkdown } from '../utils/stripMarkdown';
import { createActionId, getSessionId } from '../utils/session';

/**
 * Returns a callback that opens the Magic Apron assistant's drawer and, when given a question to
 * prepopulate, hands it over for the assistant to answer. The drawer is opened over a
 * `BroadcastChannel` and the question is published on `LIFE_CYCLE_EVENT_BUS.lifeCycle`, which
 * replays to new subscribers so the assistant receives it whenever it mounts.
 *
 * With `prepopulate: false` the drawer opens empty for the shopper to type in, and the click is
 * reported here instead — nothing is handed over, so the assistant never reports it.
 */
export const useAskMagicApron = ({ entryLocationForAnalytics = '', enabled = true } = {}) => {
  const channelRef = useRef(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
      return undefined;
    }

    // Captured in a local so teardown closes the channel this effect opened, whatever the ref holds.
    const channel = new BroadcastChannel('magic-apron-assistant');
    channelRef.current = channel;

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [enabled]);

  return useCallback(({ question, position, prepopulate = true } = {}) => {
    if (!channelRef.current) {
      return;
    }

    channelRef.current.postMessage({ type: 'MAA_OPEN_DRAWER' });

    const plainQuestion = stripMarkdown(question);

    if (!prepopulate) {
      const sessionID = getSessionId();

      window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.question-ask', {
        userQuestion: plainQuestion,
        entryLocationForAnalytics,
        sessionID,
        actionID: createActionId(sessionID)
      });
      return;
    }

    if (!plainQuestion) {
      return;
    }

    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('magic-apron-assistant.user-ask-question', {
      question: plainQuestion,
      entryLocationForAnalytics,
      messageOrigin: 'prepopulated',
      position
    });
  }, [entryLocationForAnalytics]);
};
